import type { Timestamp } from 'firebase/firestore'

/* ============================================================================
   Formatting.

   Matches `MobileFormat` in the iOS app so the same item reads identically on
   a phone and in a browser. Locale is pinned to en-GB for dates — a tour
   itinerary that renders 06/11 on one device and 11/06 on another is a missed
   lobby call, not a preference.
   ============================================================================ */

const DATE_LOCALE = 'en-GB'

/**
 * Tolerates whatever it is handed.
 *
 * `.toDate()` on a value that isn't a Timestamp throws, and a throw inside a
 * formatter takes the whole page down rather than one line of text. A field
 * that can't be read should render as an em dash.
 */
function resolve(value: Timestamp | Date | string | number | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return isNaN(parsed.getTime()) ? null : parsed
  }
  if (typeof (value as Timestamp).toDate === 'function') return (value as Timestamp).toDate()
  return null
}

/** "Fri 6 Nov" */
export function formatDay(value: Timestamp | Date | null | undefined, timeZone?: string | null) {
  const date = resolve(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat(DATE_LOCALE, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

/** "Friday 6 November 2026" */
export function formatDayLong(
  value: Timestamp | Date | null | undefined,
  timeZone?: string | null,
) {
  const date = resolve(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat(DATE_LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

/** "15:00" — 24h, in the item's own timezone when it has one. */
export function formatTime(value: Timestamp | Date | null | undefined, timeZone?: string | null) {
  const date = resolve(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat(DATE_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

/** "6 Nov" */
export function formatShortDate(
  value: Timestamp | Date | null | undefined,
  timeZone?: string | null,
) {
  const date = resolve(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat(DATE_LOCALE, {
    day: 'numeric',
    month: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

export function formatDateRange(
  start: Timestamp | Date | null | undefined,
  end: Timestamp | Date | null | undefined,
) {
  return `${formatShortDate(start)} – ${formatShortDate(end)}`
}

/** "Today" / "Tomorrow" / "In 12 days" — counted in whole calendar days. */
export function countdown(value: Timestamp | Date | null | undefined): string {
  const date = resolve(value)
  if (!date) return '—'

  const days = wholeDaysBetween(new Date(), date)
  if (days < -365) return `${Math.floor(-days / 365)} year${-days >= 730 ? 's' : ''} ago`
  if (days < -30) return `${Math.floor(-days / 30)} month${-days >= 60 ? 's' : ''} ago`
  if (days < -1) return `${-days} days ago`
  if (days === -1) return 'Yesterday'
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days < 31) return `In ${days} days`
  const months = Math.floor(days / 30)
  return `In ${months} month${months === 1 ? '' : 's'}`
}

/** Calendar-day difference, ignoring clock time. */
export function wholeDaysBetween(from: Date, to: Date): number {
  const a = startOfDay(from).getTime()
  const b = startOfDay(to).getTime()
  return Math.round((b - a) / 86_400_000)
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

/**
 * Does this instant fall on `day`?
 *
 * The instant is read in its OWN timezone and compared as year/month/day.
 * Comparing against a device-timezone midnight pulls a 16 Nov Melbourne
 * arrival onto 17 Nov for a New Zealand user — a real bug the native app hit.
 */
export function isOnDay(
  value: Timestamp | Date | null | undefined,
  day: Date,
  timeZone?: string | null,
): boolean {
  const date = resolve(value)
  if (!date) return false
  return dayKey(date, timeZone) === dayKey(day, null)
}

/** "2026-11-06" in a given zone — safe to compare and to use as a map key. */
export function dayKey(date: Date, timeZone?: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

export function formatMoney(amount: number, currency = 'NZD'): string {
  return new Intl.NumberFormat(DATE_LOCALE, { style: 'currency', currency }).format(amount)
}
